import { globalOptions } from "../../settings";

const getParentOriginHint = (): string | null => {
    try {
        return new URLSearchParams(window.location.search).get("parentOrigin");
    } catch {
        return null;
    }
};

/**
 * Returns the Connect-side route prefix (e.g. "/{tenantId}/imos") that Connect
 * passed via the `connectBasePath` query param when embedding this Veslink
 * instance, or null if it wasn't provided (not embedded via Connect).
 */
export const getConnectBasePath = (): string | null => {
    try {
        const raw = new URLSearchParams(window.location.search).get("connectBasePath");
        return raw && raw.startsWith("/") ? raw : null;
    } catch {
        return null;
    }
};

/**
 * Returns the parent origin to use as the postMessage `targetOrigin` and to
 * validate inbound `event.origin` against when Veslink is embedded.
 */
export const getConnectOrigin = (): string | null => {
    const raw: string = globalOptions.connectOrigin || "";
    const tokens = raw.split(/\s+/).filter((t: string) => t.startsWith("http"));
    if (!tokens.length) return null;

    const candidates: string[] = [];
    const hint = getParentOriginHint();
    if (hint) candidates.push(hint);
    if (document.referrer) candidates.push(document.referrer);

    for (const candidate of candidates) {
        let origin: string;
        try {
            origin = new URL(candidate).origin;
        } catch {
            continue;
        }
        if (tokens.some((t) => matchesOrigin(origin, t))) return origin;
    }
    return null;
};

/**
 * Match a concrete origin against an allowlist token. Exact tokens match
 * by equality. Wildcard tokens (`https://*.example.com`) accept any host
 * that ends in the suffix and has at least one non-empty label in place
 * of the `*` — the wildcard does not match the bare apex (`example.com`).
 */
export const matchesOrigin = (origin: string, token: string): boolean => {
    if (origin === token) return true;
    const parsed = parseWildcardToken(token);
    if (!parsed) return false;
    if (!origin.startsWith(parsed.scheme)) return false;
    const host = origin.slice(parsed.scheme.length);
    const dotSuffix = "." + parsed.suffix;
    if (!host.endsWith(dotSuffix)) return false;
    return host.length > dotSuffix.length;
};

interface ParsedWildcardToken {
    scheme: string;
    suffix: string;
}

const parseWildcardToken = (token: string): ParsedWildcardToken | null => {
    const match = token.match(/^(https?:\/\/)\*\.([^/]+)$/);
    if (!match) return null;
    return { scheme: match[1], suffix: match[2] };
};

/**
 * Posts a connect:location-changed message to the parent window so the connect-app
 * can keep its address bar / breadcrumbs in sync with Veslink's SPA route.
 *
 * No-ops silently when Veslink is not embedded in a known Connect host (either not
 * framed, or the parent origin is not on the AllowedFrameAncestors allowlist). A
 * leading '#' is stripped defensively so callers may pass raw Backbone fragments
 * without pre-processing.
 */
export const emitLocationChanged = (fragment: string): void => {
    const origin = getConnectOrigin();
    if (!origin || window.parent === window) return;
    const cleanFragment = fragment.replace(/^#/, "");
    window.parent.postMessage(
        { type: "connect:location-changed", fragment: cleanFragment },
        origin
    );
};

/**
 * Posts a connect:navigate message to the parent window when Veslink is embedded.
 * Used (via navigateOrOpen) by workspace actions that don't render a real anchor (e.g.
 * scheduling/planning board rows) to ask the connect-app to open an internal Veslink
 * route (in a new tab or the current one) rather than handling navigation locally.
 *
 * Resolves the target via getConnectOrigin (parentOrigin hint, then referrer) and posts to
 * that concrete origin — aligned with emitLocationChanged so navigate sync works in the
 * defer-auth embed. Returns true when a message was posted; returns false when not framed or
 * the parent origin can't be resolved, so navigateOrOpen can fall back to window.open.
 */
export const emitNavigate = (fragment: string, target: "_blank" | "_self" = "_blank"): boolean => {
    const origin = getConnectOrigin();
    if (!origin || window.parent === window) return false;
    const cleanFragment = fragment.replace(/^\/?#/, "");
    window.parent.postMessage(
        { type: "connect:navigate", fragment: cleanFragment, target },
        origin
    );
    return true;
};

/**
 * In embed mode, posts a connect:navigate message to the parent window. Falls back to
 * window.open when not framed or when the parent (Connect) origin can't be resolved, so a
 * click always navigates somewhere.
 * @param href - A Veslink hash URL, e.g. "#operations/voyages/VESSEL/123"
 */
export const navigateOrOpen = (href: string): void => {
    if (!emitNavigate(href)) {
        window.open(href, "_blank");
    }
};

/**
 * Asks the Connect host to remount this frame. Returns false when not framed or
 * the parent origin cannot be resolved, so reloadFrameOrSelf can fall back.
 */
export const emitFrameReload = (): boolean => {
    const origin = getConnectOrigin();
    if (!origin || window.parent === window) return false;
    window.parent.postMessage({ type: "connect:reload-frame" }, origin);
    return true;
};

/**
 * How long the host gets to act. A host that acts replaces the frame, so the
 * fallback only runs when nothing listens.
 */
const FRAME_RELOAD_FALLBACK_MS = 1500;

let pendingFallbackTimer: number | undefined;

/**
 * Embedded, window.location.reload() re-requests the current Veslink URL and
 * cannot ask the host for a token, so it cannot replay the handshake that
 * established this session. Only the host can, by replacing the frame.
 *
 * Repeat clicks re-ask, because the user can cancel a reload at the
 * unsaved-changes prompt.
 */
export const reloadFrameOrSelf = (): void => {
    if (!globalOptions.isConnectEmbedded || !emitFrameReload()) {
        window.location.reload();
        return;
    }
    if (pendingFallbackTimer !== undefined) return;

    pendingFallbackTimer = window.setTimeout(() => {
        pendingFallbackTimer = undefined;
        window.location.reload();
    }, FRAME_RELOAD_FALLBACK_MS);
};

/**
 * Rewrites a Veslink-relative hash route (e.g. "#operations/voyages/VESSEL/123")
 * into an absolute Connect-side href (e.g. "https://app.veson.com/{tenantId}/imos/operations/voyages/VESSEL/123").
 *
 * Every action on an anchor — left-click, right-click "open in new tab",
 * middle-click, hover preview, "copy link address" — reads the DOM `href`
 * attribute directly; only some of those also fire a JS `click` event that
 * code could intercept. Left as a bare hash route, all of them resolve
 * against the iframe's own document location and leak out to Veslink's
 * origin instead of staying on the Connect host. Calling this when assigning
 * `href` fixes that for every action at once, with no click interception
 * needed at all.
 */
export const resolveEmbedHref = (href: string): string => {
    if (!href.startsWith("#")) return href;
    const origin = getConnectOrigin();
    const basePath = getConnectBasePath();
    if (!origin || !basePath) return href;
    return `${origin}${basePath}/${dropSlashBeforeQuery(href.slice(1))}`;
};

// Connect redirects a path that ends in "/", and the redirect re-encodes the
// query as key=value pairs, which module args are not.
const dropSlashBeforeQuery = (route: string): string => {
    const queryAt = route.indexOf("?");
    if (queryAt === -1) return route;
    return `${route.slice(0, queryAt).replace(/\/$/, "")}${route.slice(queryAt)}`;
};

/**
 * Coarse "a person is doing something in here" ping for the Connect host's idle
 * watchdog. The host cannot observe interaction inside this iframe — DOM events
 * do not cross the frame boundary, and emitKeydown only forwards the two
 * shortcuts the host owns — so without this a user working entirely in IMOS
 * looks idle and gets signed out mid-task.
 *
 * Throttled because the host only needs to know activity happened recently, not
 * how often; it carries no event detail for the same reason.
 */
const ACTIVITY_THROTTLE_MS = 15000;
let lastActivityAt = 0;
let lastActivityPostedAt = 0;

/**
 * When someone last interacted with this iframe. Recorded on every event, unlike
 * the throttled postMessage, so local callers (the session keepalive) get an
 * exact answer rather than one rounded to the throttle window.
 */
export const getLastActivityAt = (): number => lastActivityAt;

export const emitActivity = (): void => {
    if (window.parent === window) return;
    const now = Date.now();
    lastActivityAt = now;
    if (now - lastActivityPostedAt < ACTIVITY_THROTTLE_MS) return;
    // Resolved only when about to post, not per event: this runs off wheel, which
    // fires many times a second while scrolling, and getConnectOrigin parses the
    // query string and builds URLs every call.
    const origin = getConnectOrigin();
    if (!origin) return;
    lastActivityPostedAt = now;
    window.parent.postMessage({ type: "connect:activity" }, origin);
};

/**
 * Attaches the activity listeners once, in the capture phase so a handler that
 * stops propagation cannot hide the interaction from the host.
 */
export const startActivityReporting = (): void => {
    if (!getConnectOrigin() || window.parent === window) return;
    const events: ReadonlyArray<string> = ["pointerdown", "keydown", "wheel"];
    for (const event of events) {
        document.addEventListener(event, emitActivity, { capture: true, passive: true });
    }
};

export const emitKeydown = (event: KeyboardEvent): void => {
    const origin = getConnectOrigin();
    if (!origin || window.parent === window) return;
    const { key, code, ctrlKey, metaKey, shiftKey, altKey } = event;
    window.parent.postMessage(
        { type: "connect:keydown", key, code, ctrlKey, metaKey, shiftKey, altKey },
        origin
    );
};
